import React, { useState } from 'react';
import { ChevronRight, User, Users, Zap, BarChart, AlertTriangle, Check, Maximize2, X } from 'lucide-react';
import { Founder, CompatibilityScore } from '../utils/types';
import { LinkedInIcon } from './LinkedInIcon';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend, Scatter } from 'recharts';
interface AnalysisPanelProps {
  selectedFounder: Founder | null;
  comparedFounder: Founder | null;
  compatibility: CompatibilityScore | null;
}
export const AnalysisPanel = ({
  selectedFounder,
  comparedFounder,
  compatibility
}: AnalysisPanelProps) => {
  const [collapsed, setCollapsed] = useState(false);
  const [hoveredSkill, setHoveredSkill] = useState<string | null>(null);
  const [hoveredTrajectoryPoint, setHoveredTrajectoryPoint] = useState<any>(null);
  const [debugMode, setDebugMode] = useState(false);
  const [expandedView, setExpandedView] = useState(false);
  if (collapsed) {
    return <div className="w-12 bg-[#111] border-l border-[#222] flex flex-col items-center py-4" data-id="element-8">
        <button onClick={() => setCollapsed(false)} className="p-2 rounded-full hover:bg-[#222] text-gray-400 hover:text-white" data-id="element-9">
          <ChevronRight size={20} data-id="element-10" />
        </button>
      </div>;
  }
  if (!selectedFounder) {
    return <div className="w-80 bg-[#111] border-l border-[#222] flex flex-col h-full" data-id="element-11">
        <div className="flex justify-between items-center p-4 border-b border-[#222]" data-id="element-12">
          <h2 className="text-sm font-semibold" data-id="element-13">Pairing Analysis</h2>
          <button onClick={() => setCollapsed(true)} className="p-1 rounded-full hover:bg-[#222] text-gray-400 hover:text-white" data-id="element-14">
            <ChevronRight size={16} data-id="element-15" />
          </button>
        </div>
        <div className="flex flex-col items-center justify-center h-full p-6 text-center text-gray-400" data-id="element-16">
          <Users size={40} className="mb-4 opacity-20" data-id="element-17" />
          <p className="text-sm" data-id="element-18">
            Select a founder to see compatibility analysis
          </p>
        </div>
      </div>;
  }
  // If we have a selected founder but no compared founder
  if (!comparedFounder) {
    return <div className="w-80 bg-[#111] border-l border-[#222] flex flex-col h-full overflow-auto" data-id="element-19">
        <div className="flex justify-between items-center p-4 border-b border-[#222]" data-id="element-20">
          <h2 className="text-sm font-semibold" data-id="element-21">Founder Profile</h2>
          <button onClick={() => setCollapsed(true)} className="p-1 rounded-full hover:bg-[#222] text-gray-400 hover:text-white" data-id="element-22">
            <ChevronRight size={16} data-id="element-23" />
          </button>
        </div>
        <div className="p-3 m-4 mb-2 bg-[#1a1a1a] rounded text-xs" data-id="element-24">
          <p className="text-gray-300 mb-2" data-id="element-25">
            <span className="font-semibold" data-id="element-26">Select a second founder</span> to
            analyze compatibility
          </p>
          <p className="text-gray-400" data-id="element-27">
            Use{' '}
            <span className="px-1 py-0.5 bg-[#333] rounded" data-id="element-28">Shift+Click</span>{' '}
            on another node to compare
          </p>
        </div>
        <div className="p-4" data-id="element-29">
          <div className="flex items-center mb-4" data-id="element-30">
            <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center mr-3" data-id="element-31">
              {selectedFounder.photoUrl ? <img src={selectedFounder.photoUrl} alt={selectedFounder.name} className="w-full h-full rounded-full object-cover" data-id="element-32" /> : <span className="text-lg font-bold text-white" data-id="element-33">
                  {selectedFounder.name.split(' ').map(n => n[0]).join('')}
                </span>}
            </div>
            <div data-id="element-34">
              <div className="flex items-center" data-id="element-35">
                <h3 className="font-semibold" data-id="element-36">{selectedFounder.name}</h3>
                {selectedFounder.linkedinUrl && <a href={selectedFounder.linkedinUrl} target="_blank" rel="noopener noreferrer" className="ml-2 text-blue-400 hover:text-blue-300" data-id="element-37">
                    <LinkedInIcon size={12} data-id="element-38" />
                  </a>}
              </div>
              <div className="flex items-center text-xs" data-id="element-39">
                <span className={`w-2 h-2 rounded-full mr-1 ${getArchetypeColor(selectedFounder.archetype)}`} data-id="element-40"></span>
                <span data-id="element-41">{selectedFounder.archetype}</span>
              </div>
            </div>
          </div>
          <div className="space-y-4 text-sm" data-id="element-42">
            <div data-id="element-43">
              <h4 className="text-xs text-gray-400 mb-1" data-id="element-44">Skills</h4>
              <div className="flex flex-wrap gap-1" data-id="element-45">
                {selectedFounder.skills.map(skill => <span key={skill} className="px-2 py-0.5 bg-[#222] rounded-full text-xs" data-id="element-46">
                    {skill}
                  </span>)}
              </div>
            </div>
            <div data-id="element-47">
              <h4 className="text-xs text-gray-400 mb-1" data-id="element-48">Experience</h4>
              <div className="space-y-2" data-id="element-49">
                {selectedFounder.previousCompanies.map((company, index) => <div key={`${selectedFounder.id}-${index}`} className="flex justify-between" data-id="element-50">
                    <span data-id="element-51">{company.name}</span>
                    <span className="text-gray-400" data-id="element-52">
                      {company.role} ({company.years[0]}-{company.years[1]})
                    </span>
                  </div>)}
              </div>
            </div>
            <div data-id="element-53">
              <h4 className="text-xs text-gray-400 mb-1" data-id="element-54">Education</h4>
              <div className="space-y-2" data-id="element-55">
                {selectedFounder.education.map((edu, index) => <div key={`${selectedFounder.id}-${index}`} className="flex justify-between" data-id="element-56">
                    <span data-id="element-57">{edu.institution}</span>
                    <span className="text-gray-400" data-id="element-58">
                      {edu.degree} ({edu.year})
                    </span>
                  </div>)}
              </div>
            </div>
            {selectedFounder.exitHistory.length > 0 && <div data-id="element-59">
                <h4 className="text-xs text-gray-400 mb-1" data-id="element-60">Exit History</h4>
                <div className="space-y-2" data-id="element-61">
                  {selectedFounder.exitHistory.map((exit, index) => <div key={`${selectedFounder.id}-${index}`} className="flex justify-between" data-id="element-62">
                      <span data-id="element-63">{exit.company}</span>
                      <span className="text-green-400" data-id="element-64">
                        {exit.value} ({exit.year})
                      </span>
                    </div>)}
                </div>
              </div>}
            <div className="pt-2" data-id="element-65">
              <div className="flex justify-between mb-2" data-id="element-66">
                <div className="flex items-center" data-id="element-67">
                  <Zap size={14} className="mr-1 text-yellow-400" data-id="element-68" />
                  <span className="text-xs" data-id="element-69">Velocity Score</span>
                </div>
                <span className="text-yellow-400 font-semibold" data-id="element-70">
                  {selectedFounder.velocityScore}/100
                </span>
              </div>
              <div className="w-full bg-[#222] rounded-full h-1.5" data-id="element-71">
                <div className="bg-yellow-400 h-1.5 rounded-full" style={{
                width: `${selectedFounder.velocityScore}%`
              }} data-id="element-72"></div>
              </div>
            </div>
            <div data-id="element-73">
              <div className="flex justify-between mb-2" data-id="element-74">
                <div className="flex items-center" data-id="element-75">
                  <User size={14} className="mr-1 text-blue-400" data-id="element-76" />
                  <span className="text-xs" data-id="element-77">Founder Potential</span>
                </div>
                <span className="text-blue-400 font-semibold" data-id="element-78">
                  {selectedFounder.potentialScore}/100
                </span>
              </div>
              <div className="w-full bg-[#222] rounded-full h-1.5" data-id="element-79">
                <div className="bg-blue-400 h-1.5 rounded-full" style={{
                width: `${selectedFounder.potentialScore}%`
              }} data-id="element-80"></div>
              </div>
            </div>
          </div>
        </div>
      </div>;
  }
  // Render the expanded full-screen modal view
  if (expandedView && compatibility) {
    return <>
        {/* Keep the original panel in the background */}
        <div className="w-80 bg-[#111] border-l border-[#222] flex flex-col h-full" data-id="element-81">
          <div className="flex justify-between items-center p-4 border-b border-[#222]" data-id="element-82">
            <h2 className="text-sm font-semibold" data-id="element-83">Pairing Analysis</h2>
            <button onClick={() => setCollapsed(true)} className="p-1 rounded-full hover:bg-[#222] text-gray-400 hover:text-white" data-id="element-84">
              <ChevronRight size={16} data-id="element-85" />
            </button>
          </div>
        </div>
        {/* Full-screen modal overlay */}
        <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center p-8" data-id="element-86">
          <div className="bg-[#111] border border-[#222] rounded-lg w-full max-w-6xl max-h-[90vh] overflow-auto" data-id="element-87">
            <div className="flex justify-between items-center p-4 border-b border-[#222] sticky top-0 bg-[#111] z-10" data-id="element-88">
              <h2 className="text-lg font-semibold" data-id="element-89">
                Founder Compatibility Analysis
              </h2>
              <button onClick={() => setExpandedView(false)} className="p-2 rounded-full hover:bg-[#222] text-gray-400 hover:text-white" data-id="element-90">
                <X size={20} data-id="element-91" />
              </button>
            </div>
            <div className="p-6" data-id="element-92">
              <div className="flex justify-between items-center mb-8" data-id="element-93">
                <div className="flex items-center" data-id="element-94">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mr-4" data-id="element-95">
                    {selectedFounder.photoUrl ? <img src={selectedFounder.photoUrl} alt={selectedFounder.name} className="w-full h-full rounded-full object-cover" data-id="element-96" /> : <span className="text-xl font-bold text-white bg-blue-500 w-full h-full rounded-full flex items-center justify-center" data-id="element-97">
                        {selectedFounder.name.split(' ').map(n => n[0]).join('')}
                      </span>}
                  </div>
                  <div data-id="element-98">
                    <div className="flex items-center" data-id="element-99">
                      <h3 className="text-xl font-semibold" data-id="element-100">
                        {selectedFounder.name}
                      </h3>
                      {selectedFounder.linkedinUrl && <a href={selectedFounder.linkedinUrl} target="_blank" rel="noopener noreferrer" className="ml-2 text-blue-400 hover:text-blue-300" data-id="element-101">
                          <LinkedInIcon size={16} data-id="element-102" />
                        </a>}
                    </div>
                    <div className="flex items-center text-sm" data-id="element-103">
                      <span className={`w-3 h-3 rounded-full mr-2 ${getArchetypeColor(selectedFounder.archetype)}`} data-id="element-104"></span>
                      <span data-id="element-105">{selectedFounder.archetype}</span>
                    </div>
                  </div>
                </div>
                <div className="text-3xl font-bold text-center" data-id="element-106">
                  <div className={getScoreColor(compatibility.overall)} data-id="element-107">
                    {compatibility.overall}%
                  </div>
                  <div className="text-sm text-gray-400 font-normal" data-id="element-108">
                    Compatibility
                  </div>
                </div>
                <div className="flex items-center" data-id="element-109">
                  <div data-id="element-110">
                    <div className="flex items-center" data-id="element-111">
                      <h3 className="text-xl font-semibold" data-id="element-112">
                        {comparedFounder.name}
                      </h3>
                      {comparedFounder.linkedinUrl && <a href={comparedFounder.linkedinUrl} target="_blank" rel="noopener noreferrer" className="ml-2 text-blue-400 hover:text-blue-300" data-id="element-113">
                          <LinkedInIcon size={16} data-id="element-114" />
                        </a>}
                    </div>
                    <div className="flex items-center text-sm" data-id="element-115">
                      <span className={`w-3 h-3 rounded-full mr-2 ${getArchetypeColor(comparedFounder.archetype)}`} data-id="element-116"></span>
                      <span data-id="element-117">{comparedFounder.archetype}</span>
                    </div>
                  </div>
                  <div className="w-16 h-16 rounded-full flex items-center justify-center ml-4" data-id="element-118">
                    {comparedFounder.photoUrl ? <img src={comparedFounder.photoUrl} alt={comparedFounder.name} className="w-full h-full rounded-full object-cover" data-id="element-119" /> : <span className="text-xl font-bold text-white bg-green-500 w-full h-full rounded-full flex items-center justify-center" data-id="element-120">
                        {comparedFounder.name.split(' ').map(n => n[0]).join('')}
                      </span>}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-8" data-id="element-121">
                <div data-id="element-122">
                  <h4 className="text-base font-semibold mb-4" data-id="element-123">
                    Compatibility Breakdown
                  </h4>
                  <div className="bg-[#1a1a1a] p-4 rounded mb-8" data-id="element-124">
                    <div className="space-y-4" data-id="element-125">
                      <div data-id="element-126">
                        <div className="flex justify-between items-center mb-1" data-id="element-127">
                          <span data-id="element-128">Skills Complementarity</span>
                          <span className={getScoreColor(compatibility.skills)} data-id="element-129">
                            {compatibility.skills}%
                          </span>
                        </div>
                        <div className="w-full bg-[#333] rounded-full h-2" data-id="element-130">
                          <div className={`${getScoreColorBg(compatibility.skills)} h-2 rounded-full`} style={{
                          width: `${compatibility.skills}%`
                        }} data-id="element-131"></div>
                        </div>
                      </div>
                      <div data-id="element-132">
                        <div className="flex justify-between items-center mb-1" data-id="element-133">
                          <span data-id="element-134">Working Style</span>
                          <span className={getScoreColor(compatibility.workingStyle)} data-id="element-135">
                            {compatibility.workingStyle}%
                          </span>
                        </div>
                        <div className="w-full bg-[#333] rounded-full h-2" data-id="element-136">
                          <div className={`${getScoreColorBg(compatibility.workingStyle)} h-2 rounded-full`} style={{
                          width: `${compatibility.workingStyle}%`
                        }} data-id="element-137"></div>
                        </div>
                      </div>
                      <div data-id="element-138">
                        <div className="flex justify-between items-center mb-1" data-id="element-139">
                          <span data-id="element-140">Velocity Match</span>
                          <span className={getScoreColor(compatibility.velocityMatch)} data-id="element-141">
                            {compatibility.velocityMatch}%
                          </span>
                        </div>
                        <div className="w-full bg-[#333] rounded-full h-2" data-id="element-142">
                          <div className={`${getScoreColorBg(compatibility.velocityMatch)} h-2 rounded-full`} style={{
                          width: `${compatibility.velocityMatch}%`
                        }} data-id="element-143"></div>
                        </div>
                      </div>
                      <div data-id="element-144">
                        <div className="flex justify-between items-center mb-1" data-id="element-145">
                          <span data-id="element-146">Archetype Balance</span>
                          <span className={getScoreColor(compatibility.archetypeBalance)} data-id="element-147">
                            {compatibility.archetypeBalance}%
                          </span>
                        </div>
                        <div className="w-full bg-[#333] rounded-full h-2" data-id="element-148">
                          <div className={`${getScoreColorBg(compatibility.archetypeBalance)} h-2 rounded-full`} style={{
                          width: `${compatibility.archetypeBalance}%`
                        }} data-id="element-149"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mb-8" data-id="element-150">
                    <h4 className="text-base font-semibold mb-4" data-id="element-151">
                      Complementary Skills
                    </h4>
                    <div className="h-72 bg-[#1a1a1a] p-4 rounded" data-id="element-152">
                      <ResponsiveContainer width="100%" height="100%" data-id="element-153">
                        <RadarChart outerRadius={120} data={getSkillsRadarData(selectedFounder, comparedFounder)} data-id="element-154">
                          <PolarGrid stroke="#333" data-id="element-155" />
                          <PolarAngleAxis dataKey="subject" tick={props => {
                          const {
                            x,
                            y,
                            payload,
                            textAnchor,
                            stroke
                          } = props;
                          const isHovered = hoveredSkill === payload.value;
                          return <text x={x} y={y} textAnchor={textAnchor} stroke={stroke} fill={isHovered ? '#fff' : '#999'} className={`text-xs cursor-pointer ${isHovered ? 'font-bold' : ''}`} onMouseEnter={() => setHoveredSkill(payload.value)} onMouseLeave={() => setHoveredSkill(null)} data-id="element-157">
                                  {payload.value}
                                </text>;
                        }} data-id="element-156" />
                          <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{
                          fill: '#999',
                          fontSize: 10
                        }} data-id="element-158" />
                          <Radar name={selectedFounder.name} dataKey="A" stroke="#8884d8" fill="#8884d8" fillOpacity={0.5} data-id="element-159" />
                          <Radar name={comparedFounder.name} dataKey="B" stroke="#82ca9d" fill="#82ca9d" fillOpacity={0.5} data-id="element-160" />
                        </RadarChart>
                      </ResponsiveContainer>
                      {/* Hover information below the chart */}
                      {hoveredSkill && <div className="mt-2 bg-[#222] p-3 rounded text-sm border border-[#333] animate-fadeIn z-50 relative" data-id="element-161">
                          <div className="mb-1" data-id="element-162">
                            <span className="font-semibold" data-id="element-163">
                              {hoveredSkill}:
                            </span>{' '}
                            {getSkillsRadarData(selectedFounder, comparedFounder).find(item => item.subject === hoveredSkill)?.value}
                            /10
                          </div>
                          <div className="flex items-center mb-1" data-id="element-164">
                            <span className="w-3 h-3 bg-[#8884d8] rounded-full mr-2" data-id="element-165"></span>
                            <span data-id="element-166">
                              {selectedFounder.name}:{' '}
                              {getSkillsRadarData(selectedFounder, comparedFounder).find(item => item.subject === hoveredSkill)?.A}
                            </span>
                          </div>
                          <div className="flex items-center mb-1" data-id="element-167">
                            <span className="w-3 h-3 bg-[#82ca9d] rounded-full mr-2" data-id="element-168"></span>
                            <span data-id="element-169">
                              {comparedFounder.name}:{' '}
                              {getSkillsRadarData(selectedFounder, comparedFounder).find(item => item.subject === hoveredSkill)?.B}
                            </span>
                          </div>
                          <p className="text-gray-300 mt-2" data-id="element-170">
                            {getSkillsRadarData(selectedFounder, comparedFounder).find(item => item.subject === hoveredSkill)?.description}
                          </p>
                        </div>}
                    </div>
                  </div>
                  <div data-id="element-171">
                    <h4 className="text-base font-semibold mb-4" data-id="element-172">
                      Key Insights
                    </h4>
                    <div className="grid grid-cols-1 gap-3" data-id="element-173">
                      {compatibility.details.strengths.map((strength, i) => <div key={i} className="flex p-3 bg-[#1a1a1a] rounded" data-id="element-174">
                          <Check size={18} className="mr-3 text-green-400 flex-shrink-0" data-id="element-175" />
                          <span className="text-sm" data-id="element-176">{strength}</span>
                        </div>)}
                      {compatibility.details.watchOuts.map((watchOut, i) => <div key={i} className="flex p-3 bg-[#1a1a1a] rounded" data-id="element-177">
                          <AlertTriangle size={18} className="mr-3 text-yellow-400 flex-shrink-0" data-id="element-178" />
                          <span className="text-sm" data-id="element-179">{watchOut}</span>
                        </div>)}
                    </div>
                  </div>
                </div>
                {isDivannyKevinPair(selectedFounder, comparedFounder) && <div data-id="element-180">
                    <h4 className="text-base font-semibold mb-4" data-id="element-181">
                      Career Trajectory & Velocity
                    </h4>
                    <div className="bg-[#1a1a1a] p-4 rounded mb-8" data-id="element-182">
                      <div className="flex justify-between items-center mb-4" data-id="element-183">
                        <button onClick={() => setDebugMode(!debugMode)} className="text-xs text-gray-500 hover:text-gray-300 px-2 py-1 bg-[#222] rounded" data-id="element-184">
                          {debugMode ? 'Hide Debug' : 'Debug'}
                        </button>
                      </div>
                      <div className="h-80" data-id="element-185">
                        <ResponsiveContainer width="100%" height="100%" data-id="element-186">
                          <LineChart margin={{
                        top: 20,
                        right: 10,
                        bottom: 20,
                        left: 10
                      }} onMouseLeave={() => setHoveredTrajectoryPoint(null)} data-id="element-187">
                            {/* Add grid lines */}
                            <CartesianGrid stroke="#333" strokeDasharray="3 3" vertical={false} data-id="element-188" />
                            {/* X-axis (years) */}
                            <XAxis dataKey="year" type="number" domain={[2012, 2024]} tick={{
                          fill: '#999',
                          fontSize: 10
                        }} ticks={[2012, 2016, 2020, 2024]} label={{
                          value: 'Year',
                          position: 'insideBottomRight',
                          offset: -5,
                          fill: '#999',
                          fontSize: 10,
                          dy: 10
                        }} data-id="element-189" />
                            {/* Y-axis (company size/headcount) */}
                            <YAxis domain={[0, 5]} tick={{
                          fill: '#999',
                          fontSize: 10
                        }} tickCount={6} tickFormatter={value => {
                          if (value === 0) return '0';
                          if (value === 1) return '10^2';
                          if (value === 2) return '10^3';
                          if (value === 3) return '10^4';
                          if (value === 4) return '10^5';
                          if (value === 5) return '10^6';
                          return '';
                        }} label={{
                          value: 'Log value (company value / revenue mid point)',
                          angle: -90,
                          position: 'insideLeft',
                          fill: '#999',
                          fontSize: 10
                        }} data-id="element-190" />
                            {/* Tooltip */}
                            <Tooltip content={({
                          active,
                          payload
                        }) => {
                          if (active && payload && payload.length) {
                            const data = payload[0].payload;
                            const founderName = payload[0].name;
                            return <div className="bg-[#222] p-2 rounded text-xs border border-[#333]" data-id="element-192">
                                      <p className="font-semibold text-gray-400" data-id="element-193">
                                        {founderName}
                                      </p>
                                      <p className="font-semibold" data-id="element-194">
                                        {data.year} • {data.company}
                                      </p>
                                      <p className="text-gray-300" data-id="element-195">
                                        — {data.role}
                                      </p>
                                      <p className="text-gray-400" data-id="element-196">
                                        Employees:{' '}
                                        {getOrgSizeDescription(data.org_size_entry_bucket)}
                                      </p>
                                      <p className="text-gray-400" data-id="element-197">
                                        Value:{' '}
                                        {getValuationDescription(data.valuation_bucket)}
                                      </p>
                                      {debugMode && data.src_notes && <p className="text-gray-500 mt-1 text-[10px]" data-id="element-198">
                                          {data.src_notes}
                                        </p>}
                                    </div>;
                          }
                          return null;
                        }} data-id="element-191" />
                            {/* Line for first founder */}
                            <Line name={selectedFounder.name} data={getTrajectoryData(selectedFounder, comparedFounder).founder1} dataKey="org_size_entry_bucket" stroke="#8884d8" strokeWidth={2} dot={false} activeDot={false} isAnimationActive={false} connectNulls={true} data-id="element-199" />
                            {/* Line for second founder */}
                            <Line name={comparedFounder.name} data={getTrajectoryData(selectedFounder, comparedFounder).founder2} dataKey="org_size_entry_bucket" stroke="#82ca9d" strokeWidth={2} dot={false} activeDot={false} isAnimationActive={false} connectNulls={true} data-id="element-200" />
                            {/* X markers for data points instead of circles/polygons */}
                            <Scatter name={selectedFounder.name} data={getTrajectoryData(selectedFounder, comparedFounder).founder1} fill="#8884d8" shape={props => {
                          const {
                            cx,
                            cy,
                            payload
                          } = props;
                          if (!cx || !cy || cy <= 0) return null;
                          const size = 6;
                          return <g data-id="element-202">
                                    <line x1={cx - size} y1={cy - size} x2={cx + size} y2={cy + size} stroke="#8884d8" strokeWidth={2} data-id="element-203" />
                                    <line x1={cx - size} y1={cy + size} x2={cx + size} y2={cy - size} stroke="#8884d8" strokeWidth={2} data-id="element-204" />
                                  </g>;
                        }} dataKey="org_size_entry_bucket" isAnimationActive={false} data-id="element-201" />
                            {/* X markers for second founder */}
                            <Scatter name={comparedFounder.name} data={getTrajectoryData(selectedFounder, comparedFounder).founder2} fill="#82ca9d" shape={props => {
                          const {
                            cx,
                            cy,
                            payload
                          } = props;
                          if (!cx || !cy || cy <= 0) return null;
                          const size = 6;
                          return <g data-id="element-206">
                                    <line x1={cx - size} y1={cy - size} x2={cx + size} y2={cy + size} stroke="#82ca9d" strokeWidth={2} data-id="element-207" />
                                    <line x1={cx - size} y1={cy + size} x2={cx + size} y2={cy - size} stroke="#82ca9d" strokeWidth={2} data-id="element-208" />
                                  </g>;
                        }} dataKey="org_size_entry_bucket" isAnimationActive={false} data-id="element-205" />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                      {/* Velocity match insights directly below the chart */}
                      <div className="mt-4 border-t border-[#333] pt-4" data-id="element-209">
                        <p className="font-semibold mb-2" data-id="element-210">
                          Velocity Match Insights:
                        </p>
                        <div className="grid grid-cols-1 gap-3" data-id="element-211">
                          <div className="flex" data-id="element-212">
                            <span className="text-sm" data-id="element-213">
                              Both have &lt;50-person experience (Seed Startup,
                              Breakthrough Startup) → comfort with chaos.
                            </span>
                          </div>
                          <div className="flex" data-id="element-214">
                            <span className="text-sm" data-id="element-215">
                              Both have 1000+ person / multi-$-bn exposure
                              (Unicorn Startup late-stage, Meta, Unicorn
                              Startup).
                            </span>
                          </div>
                          <div className="flex" data-id="element-216">
                            <span className="text-sm" data-id="element-217">
                              Each joined companies just before steep revenue
                              curves.
                            </span>
                          </div>
                          <div className="flex" data-id="element-218">
                            <span className="text-sm" data-id="element-219">
                              Non-overlapping domains: enterprise DevOps vs.
                              fintech infrastructure.
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>}
                {!isDivannyKevinPair(selectedFounder, comparedFounder) && <div data-id="element-220">
                    <h4 className="text-base font-semibold mb-4" data-id="element-221">
                      Shared History Timeline
                    </h4>
                    <div className="bg-[#1a1a1a] p-4 rounded mb-8" data-id="element-222">
                      <div className="h-80" data-id="element-223">
                        <ResponsiveContainer width="100%" height="100%" data-id="element-224">
                          <LineChart margin={{
                        top: 5,
                        right: 30,
                        bottom: 20,
                        left: 0
                      }} data-id="element-225">
                            <YAxis type="category" dataKey="company" width={100} axisLine={{
                          stroke: '#333'
                        }} tick={{
                          fill: '#999',
                          fontSize: 12
                        }} data-id="element-226" />
                            <XAxis type="number" dataKey="year" domain={[Math.min(...selectedFounder.previousCompanies.map(c => c.years[0]), ...comparedFounder.previousCompanies.map(c => c.years[0])), Math.max(...selectedFounder.previousCompanies.map(c => c.years[1]), ...comparedFounder.previousCompanies.map(c => c.years[1]))]} tick={{
                          fill: '#999',
                          fontSize: 12
                        }} data-id="element-227" />
                            <CartesianGrid stroke="#333" strokeDasharray="3 3" data-id="element-228" />
                            <Line type="monotone" dataKey="value" data={getTimelineDataForChart(selectedFounder)} stroke="#8884d8" strokeWidth={2} dot={{
                          r: 5,
                          fill: '#8884d8'
                        }} activeDot={{
                          r: 7,
                          fill: '#8884d8'
                        }} name={selectedFounder.name} data-id="element-229" />
                            <Line type="monotone" dataKey="value" data={getTimelineDataForChart(comparedFounder)} stroke="#82ca9d" strokeWidth={2} dot={{
                          r: 5,
                          fill: '#82ca9d'
                        }} activeDot={{
                          r: 7,
                          fill: '#82ca9d'
                        }} name={comparedFounder.name} data-id="element-230" />
                            <Tooltip content={({
                          active,
                          payload
                        }) => {
                          if (active && payload && payload.length) {
                            return <div className="bg-[#222] p-3 rounded text-sm border border-[#333]" data-id="element-232">
                                      <p className="font-semibold" data-id="element-233">
                                        {payload[0].payload.year}
                                      </p>
                                      <div className="mt-2" data-id="element-234">
                                        <p className="font-semibold" style={{
                                  color: payload[0].color
                                }} data-id="element-235">
                                          {payload[0].name}
                                        </p>
                                        <p data-id="element-236">{payload[0].payload.company}</p>
                                        <p className="text-gray-300" data-id="element-237">
                                          {payload[0].payload.role}
                                        </p>
                                      </div>
                                    </div>;
                          }
                          return null;
                        }} data-id="element-231" />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                      {/* Legend */}
                      <div className="flex justify-center mt-4 space-x-6" data-id="element-238">
                        <div className="flex items-center" data-id="element-239">
                          <div className="w-3 h-3 bg-[#8884d8] mr-2 rounded-full" data-id="element-240"></div>
                          <span className="text-sm" data-id="element-241">
                            {selectedFounder.name}
                          </span>
                        </div>
                        <div className="flex items-center" data-id="element-242">
                          <div className="w-3 h-3 bg-[#82ca9d] mr-2 rounded-full" data-id="element-243"></div>
                          <span className="text-sm" data-id="element-244">
                            {comparedFounder.name}
                          </span>
                        </div>
                      </div>
                      {/* Work History Insights */}
                      <div className="mt-4 border-t border-[#333] pt-4" data-id="element-245">
                        <p className="font-semibold mb-2" data-id="element-246">
                          Work History Insights:
                        </p>
                        <div className="grid grid-cols-1 gap-3" data-id="element-247">
                          <div className="flex" data-id="element-248">
                            <span className="text-sm" data-id="element-249">
                              {selectedFounder.name} has{' '}
                              {selectedFounder.yearsExperience} years of
                              experience across{' '}
                              {selectedFounder.previousCompanies.length}{' '}
                              companies.
                            </span>
                          </div>
                          <div className="flex" data-id="element-250">
                            <span className="text-sm" data-id="element-251">
                              {comparedFounder.name} has{' '}
                              {comparedFounder.yearsExperience} years of
                              experience across{' '}
                              {comparedFounder.previousCompanies.length}{' '}
                              companies.
                            </span>
                          </div>
                          <div className="flex" data-id="element-252">
                            <span className="text-sm" data-id="element-253">
                              {selectedFounder.exitHistory.length > 0 || comparedFounder.exitHistory.length > 0 ? `Combined exit history: ${selectedFounder.exitHistory.length + comparedFounder.exitHistory.length} exits.` : 'No previous exits between the founders.'}
                            </span>
                          </div>
                          <div className="flex" data-id="element-254">
                            <span className="text-sm" data-id="element-255">
                              {selectedFounder.archetype} /{' '}
                              {comparedFounder.archetype} pairing with
                              complementary skill sets.
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </>;
  }
  // Full compatibility analysis view (original sidebar)
  return <div className="w-80 bg-[#111] border-l border-[#222] flex flex-col h-full overflow-auto" data-id="element-256">
      <div className="flex justify-between items-center p-4 border-b border-[#222]" data-id="element-257">
        <h2 className="text-sm font-semibold" data-id="element-258">Pairing Analysis</h2>
        <div className="flex items-center" data-id="element-259">
          <button onClick={() => setExpandedView(true)} className="p-1 rounded-full hover:bg-[#222] text-gray-400 hover:text-white mr-2" title="Expand analysis" data-id="element-260">
            <Maximize2 size={16} data-id="element-261" />
          </button>
          <button onClick={() => setCollapsed(true)} className="p-1 rounded-full hover:bg-[#222] text-gray-400 hover:text-white" data-id="element-262">
            <ChevronRight size={16} data-id="element-263" />
          </button>
        </div>
      </div>
      <div className="p-4" data-id="element-264">
        <div className="flex items-center justify-between mb-4" data-id="element-265">
          <div className="flex items-center" data-id="element-266">
            <div className="w-10 h-10 rounded-full flex items-center justify-center mr-2" data-id="element-267">
              {selectedFounder.photoUrl ? <img src={selectedFounder.photoUrl} alt={selectedFounder.name} className="w-full h-full rounded-full object-cover" data-id="element-268" /> : <span className="text-sm font-bold text-white bg-blue-500 w-full h-full rounded-full flex items-center justify-center" data-id="element-269">
                  {selectedFounder.name.split(' ').map(n => n[0]).join('')}
                </span>}
            </div>
            <div data-id="element-270">
              <div className="flex items-center" data-id="element-271">
                <h3 className="text-sm font-semibold" data-id="element-272">
                  {selectedFounder.name}
                </h3>
                {selectedFounder.linkedinUrl && <a href={selectedFounder.linkedinUrl} target="_blank" rel="noopener noreferrer" className="ml-2 text-blue-400 hover:text-blue-300" data-id="element-273">
                    <LinkedInIcon size={12} data-id="element-274" />
                  </a>}
              </div>
              <div className="flex items-center text-xs" data-id="element-275">
                <span className={`w-2 h-2 rounded-full mr-1 ${getArchetypeColor(selectedFounder.archetype)}`} data-id="element-276"></span>
                <span data-id="element-277">{selectedFounder.archetype}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center" data-id="element-278">
            <div className="flex items-center" data-id="element-279">
              <div className="w-10 h-10 rounded-full flex items-center justify-center ml-2" data-id="element-280">
                {comparedFounder.photoUrl ? <img src={comparedFounder.photoUrl} alt={comparedFounder.name} className="w-full h-full rounded-full object-cover" data-id="element-281" /> : <span className="text-sm font-bold text-white bg-green-500 w-full h-full rounded-full flex items-center justify-center" data-id="element-282">
                    {comparedFounder.name.split(' ').map(n => n[0]).join('')}
                  </span>}
              </div>
              <div data-id="element-283">
                <div className="flex items-center" data-id="element-284">
                  <h3 className="text-sm font-semibold" data-id="element-285">
                    {comparedFounder.name}
                  </h3>
                  {comparedFounder.linkedinUrl && <a href={comparedFounder.linkedinUrl} target="_blank" rel="noopener noreferrer" className="ml-2 text-blue-400 hover:text-blue-300" data-id="element-286">
                      <LinkedInIcon size={12} data-id="element-287" />
                    </a>}
                </div>
                <div className="flex items-center text-xs" data-id="element-288">
                  <span className={`w-2 h-2 rounded-full mr-1 ${getArchetypeColor(comparedFounder.archetype)}`} data-id="element-289"></span>
                  <span data-id="element-290">{comparedFounder.archetype}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        {compatibility && <div className="space-y-4" data-id="element-291">
            <div className="bg-[#1a1a1a] p-3 rounded" data-id="element-292">
              <div className="flex justify-between items-center mb-2" data-id="element-293">
                <h4 className="text-sm font-semibold" data-id="element-294">Compatibility Score</h4>
                <div className={`text-lg font-bold ${getScoreColor(compatibility.overall)}`} data-id="element-295">
                  {compatibility.overall}%
                </div>
              </div>
              <div className="space-y-2 text-xs" data-id="element-296">
                <div className="flex justify-between items-center" data-id="element-297">
                  <span data-id="element-298">Skills Complementarity</span>
                  <span className={getScoreColor(compatibility.skills)} data-id="element-299">
                    {compatibility.skills}%
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-300">
                  <span data-id="element-301">Working Style</span>
                  <span className={getScoreColor(compatibility.workingStyle)} data-id="element-302">
                    {compatibility.workingStyle}%
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-303">
                  <span data-id="element-304">Velocity Match</span>
                  <span className={getScoreColor(compatibility.velocityMatch)} data-id="element-305">
                    {compatibility.velocityMatch}%
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-306">
                  <span data-id="element-307">Archetype Balance</span>
                  <span className={getScoreColor(compatibility.archetypeBalance)} data-id="element-308">
                    {compatibility.archetypeBalance}%
                  </span>
                </div>
              </div>
            </div>
            <div data-id="element-309">
              <h4 className="text-xs text-gray-400 mb-2" data-id="element-310">
                Complementary Skills
              </h4>
              <div className="h-36" data-id="element-311">
                <ResponsiveContainer width="100%" height="100%" data-id="element-312">
                  <RadarChart outerRadius={60} data={getSkillsRadarData(selectedFounder, comparedFounder)} data-id="element-313">
                    <PolarGrid stroke="#333" data-id="element-314" />
                    <PolarAngleAxis dataKey="subject" tick={props => {
                  const {
                    x,
                    y,
                    payload,
                    textAnchor,
                    stroke
                  } = props;
                  const isHovered = hoveredSkill === payload.value;
                  return <text x={x} y={y} textAnchor={textAnchor} stroke={stroke} fill={isHovered ? '#fff' : '#999'} className={`text-xs cursor-pointer ${isHovered ? 'font-bold' : ''}`} onMouseEnter={() => setHoveredSkill(payload.value)} onMouseLeave={() => setHoveredSkill(null)} data-id="element-316">
                            {payload.value}
                          </text>;
                }} data-id="element-315" />
                    <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{
                  fill: '#999',
                  fontSize: 10
                }} data-id="element-317" />
                    <Radar name={selectedFounder.name} dataKey="A" stroke="#8884d8" fill="#8884d8" fillOpacity={0.5} data-id="element-318" />
                    <Radar name={comparedFounder.name} dataKey="B" stroke="#82ca9d" fill="#82ca9d" fillOpacity={0.5} data-id="element-319" />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
              {/* Hover information below the chart */}
              {hoveredSkill && <div className="mt-2 bg-[#1a1a1a] p-2 rounded text-xs border border-[#333] animate-fadeIn" data-id="element-320">
                  <div className="mb-1" data-id="element-321">
                    <span className="font-semibold" data-id="element-322">{hoveredSkill}:</span>{' '}
                    {getSkillsRadarData(selectedFounder, comparedFounder).find(item => item.subject === hoveredSkill)?.value}
                    /10
                  </div>
                  <div className="flex items-center mb-1" data-id="element-323">
                    <span className="w-2 h-2 bg-[#8884d8] rounded-full mr-1" data-id="element-324"></span>
                    <span data-id="element-325">
                      {selectedFounder.name}:{' '}
                      {getSkillsRadarData(selectedFounder, comparedFounder).find(item => item.subject === hoveredSkill)?.A}
                    </span>
                  </div>
                  <div className="flex items-center mb-1" data-id="element-326">
                    <span className="w-2 h-2 bg-[#82ca9d] rounded-full mr-1" data-id="element-327"></span>
                    <span data-id="element-328">
                      {comparedFounder.name}:{' '}
                      {getSkillsRadarData(selectedFounder, comparedFounder).find(item => item.subject === hoveredSkill)?.B}
                    </span>
                  </div>
                  <p className="text-gray-300 mt-1" data-id="element-329">
                    {getSkillsRadarData(selectedFounder, comparedFounder).find(item => item.subject === hoveredSkill)?.description}
                  </p>
                </div>}
            </div>
            {isDivannyKevinPair(selectedFounder, comparedFounder) && <div data-id="element-330">
                <div className="flex justify-between items-center" data-id="element-331">
                  <h4 className="text-xs text-gray-400 mb-2" data-id="element-332">
                    Career Trajectory & Velocity
                  </h4>
                  <button onClick={() => setDebugMode(!debugMode)} className="text-xs text-gray-500 hover:text-gray-300" data-id="element-333">
                    {debugMode ? 'Hide Debug' : 'Debug'}
                  </button>
                </div>
                <div className="h-60" data-id="element-334">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-335">
                    <LineChart margin={{
                top: 20,
                right: 10,
                bottom: 20,
                left: 10
              }} onMouseLeave={() => setHoveredTrajectoryPoint(null)} data-id="element-336">
                      {/* Add grid lines */}
                      <CartesianGrid stroke="#333" strokeDasharray="3 3" vertical={false} data-id="element-337" />
                      {/* X-axis (years) */}
                      <XAxis dataKey="year" type="number" domain={[2012, 2024]} tick={{
                  fill: '#999',
                  fontSize: 10
                }} ticks={[2012, 2016, 2020, 2024]} label={{
                  value: 'Year',
                  position: 'insideBottomRight',
                  offset: -5,
                  fill: '#999',
                  fontSize: 10,
                  dy: 10
                }} data-id="element-338" />
                      {/* Y-axis (company size/headcount) */}
                      <YAxis domain={[0, 5]} tick={{
                  fill: '#999',
                  fontSize: 10
                }} tickCount={6} tickFormatter={value => {
                  if (value === 0) return '0';
                  if (value === 1) return '10^2';
                  if (value === 2) return '10^3';
                  if (value === 3) return '10^4';
                  if (value === 4) return '10^5';
                  if (value === 5) return '10^6';
                  return '';
                }} label={{
                  value: 'Log value (company value / revenue mid point)',
                  angle: -90,
                  position: 'insideLeft',
                  fill: '#999',
                  fontSize: 10
                }} data-id="element-339" />
                      {/* Tooltip */}
                      <Tooltip content={({
                  active,
                  payload
                }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    const founderName = payload[0].name;
                    return <div className="bg-[#222] p-2 rounded text-xs border border-[#333]" data-id="element-341">
                                <p className="font-semibold text-gray-400" data-id="element-342">
                                  {founderName}
                                </p>
                                <p className="font-semibold" data-id="element-343">
                                  {data.year} • {data.company}
                                </p>
                                <p className="text-gray-300" data-id="element-344">— {data.role}</p>
                                <p className="text-gray-400" data-id="element-345">
                                  Employees:{' '}
                                  {getOrgSizeDescription(data.org_size_entry_bucket)}
                                </p>
                                <p className="text-gray-400" data-id="element-346">
                                  Value:{' '}
                                  {getValuationDescription(data.valuation_bucket)}
                                </p>
                                {debugMode && data.src_notes && <p className="text-gray-500 mt-1 text-[10px]" data-id="element-347">
                                    {data.src_notes}
                                  </p>}
                              </div>;
                  }
                  return null;
                }} data-id="element-340" />
                      {/* Line for first founder */}
                      <Line name={selectedFounder.name} data={getTrajectoryData(selectedFounder, comparedFounder).founder1} dataKey="org_size_entry_bucket" stroke="#8884d8" strokeWidth={2} dot={false} activeDot={false} isAnimationActive={false} connectNulls={true} data-id="element-348" />
                      {/* Line for second founder */}
                      <Line name={comparedFounder.name} data={getTrajectoryData(selectedFounder, comparedFounder).founder2} dataKey="org_size_entry_bucket" stroke="#82ca9d" strokeWidth={2} dot={false} activeDot={false} isAnimationActive={false} connectNulls={true} data-id="element-349" />
                      {/* X markers for data points instead of circles/polygons */}
                      <Scatter name={selectedFounder.name} data={getTrajectoryData(selectedFounder, comparedFounder).founder1} fill="#8884d8" shape={props => {
                  const {
                    cx,
                    cy,
                    payload
                  } = props;
                  if (!cx || !cy || cy <= 0) return null;
                  const size = 6;
                  return <g data-id="element-351">
                              <line x1={cx - size} y1={cy - size} x2={cx + size} y2={cy + size} stroke="#8884d8" strokeWidth={2} data-id="element-352" />
                              <line x1={cx - size} y1={cy + size} x2={cx + size} y2={cy - size} stroke="#8884d8" strokeWidth={2} data-id="element-353" />
                              <text x={cx} y={cy - size - 5} textAnchor="middle" fill="#8884d8" fontSize={10} data-id="element-354">
                                {payload.company.split(' ')[0]}
                              </text>
                            </g>;
                }} dataKey="org_size_entry_bucket" isAnimationActive={false} data-id="element-350" />
                      {/* X markers for second founder */}
                      <Scatter name={comparedFounder.name} data={getTrajectoryData(selectedFounder, comparedFounder).founder2} fill="#82ca9d" shape={props => {
                  const {
                    cx,
                    cy,
                    payload
                  } = props;
                  if (!cx || !cy || cy <= 0) return null;
                  const size = 6;
                  return <g data-id="element-356">
                              <line x1={cx - size} y1={cy - size} x2={cx + size} y2={cy + size} stroke="#82ca9d" strokeWidth={2} data-id="element-357" />
                              <line x1={cx - size} y1={cy + size} x2={cx + size} y2={cy - size} stroke="#82ca9d" strokeWidth={2} data-id="element-358" />
                              <text x={cx} y={cy - size - 5} textAnchor="middle" fill="#82ca9d" fontSize={10} data-id="element-359">
                                {payload.company.split(' ')[0]}
                              </text>
                            </g>;
                }} dataKey="org_size_entry_bucket" isAnimationActive={false} data-id="element-355" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                {/* Velocity match insights */}
                <div className="mt-3 bg-[#1a1a1a] p-2 rounded text-xs" data-id="element-360">
                  <p className="font-semibold mb-1" data-id="element-361">Velocity Match Insights:</p>
                  <ul className="space-y-1 text-gray-300" data-id="element-362">
                    <li data-id="element-363">
                      • Both have &lt;50-person experience (Seed Startup,
                      Breakthrough Startup) → comfort with chaos.
                    </li>
                    <li data-id="element-364">
                      • Both have 1000+ person / multi-$-bn exposure (Unicorn
                      Startup late-stage, Meta, Unicorn Startup).
                    </li>
                    <li data-id="element-365">
                      • Each joined companies just before steep revenue curves.
                    </li>
                    <li data-id="element-366">
                      • Non-overlapping domains: enterprise DevOps vs. fintech
                      infrastructure.
                    </li>
                  </ul>
                </div>
              </div>}
            {!isDivannyKevinPair(selectedFounder, comparedFounder) && <div data-id="element-367">
                <h4 className="text-xs text-gray-400 mb-2" data-id="element-368">
                  Shared History Timeline
                </h4>
                <div className="h-36" data-id="element-369">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-370">
                    <LineChart data={generateTimelineData(selectedFounder, comparedFounder)} margin={{
                top: 5,
                right: 5,
                bottom: 5,
                left: 5
              }} data-id="element-371">
                      <XAxis dataKey="year" stroke="#666" tick={{
                  fill: '#999',
                  fontSize: 10
                }} data-id="element-372" />
                      <YAxis type="category" tick={{
                  fill: '#999',
                  fontSize: 10
                }} data-id="element-373" />
                      <Tooltip contentStyle={{
                  background: '#222',
                  border: 'none',
                  borderRadius: '4px'
                }} itemStyle={{
                  color: '#fff'
                }} data-id="element-374" />
                      <Line type="monotone" dataKey="founder1" stroke="#8884d8" dot={{
                  r: 3
                }} activeDot={{
                  r: 5
                }} data-id="element-375" />
                      <Line type="monotone" dataKey="founder2" stroke="#82ca9d" dot={{
                  r: 3
                }} activeDot={{
                  r: 5
                }} data-id="element-376" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>}
          </div>}
      </div>
    </div>;
};
// Helper functions
const getArchetypeColor = (archetype: string): string => {
  switch (archetype) {
    case 'CEO':
      return 'bg-purple-500';
    case 'CTO':
      return 'bg-green-500';
    default:
      return 'bg-gray-500';
  }
};
const getScoreColor = (score: number): string => {
  if (score >= 90) return 'text-green-400';
  if (score >= 80) return 'text-green-500';
  if (score >= 70) return 'text-yellow-400';
  if (score >= 60) return 'text-orange-400';
  return 'text-red-400';
};
const getScoreColorBg = (score: number): string => {
  if (score >= 90) return 'bg-green-400';
  if (score >= 80) return 'bg-green-500';
  if (score >= 70) return 'bg-yellow-400';
  if (score >= 60) return 'bg-orange-400';
  return 'bg-red-400';
};
const getSkillLevel = (founder: Founder, skill: string): number => {
  if (founder.archetype === skill) return 90;
  const skillMap: Record<string, string[]> = {
    CEO: ['Strategy', 'Vision', 'Product Vision', 'Innovation', 'Business Development', 'Sales', 'Marketing', 'Partnerships'],
    CTO: ['Engineering', 'Architecture', 'AI/ML', 'Cybersecurity', 'Operations', 'Product Management', 'Scaling', 'Process']
  };
  const relevantSkills = skillMap[skill] || [];
  const matchCount = founder.skills.filter(s => relevantSkills.some(rs => s.includes(rs))).length;
  return Math.min(85, matchCount * 25);
};
// Add this new function to generate radar data with descriptions
const getSkillsRadarData = (founder1: Founder, founder2: Founder) => {
  // Special case for Divanny Lamas and Kevin Chan
  if (founder1.id === '13' && founder2.id === '9' || founder1.id === '9' && founder2.id === '13') {
    return [{
      subject: 'Technical',
      A: founder1.id === '13' ? 60 : 100,
      B: founder2.id === '13' ? 60 : 100,
      value: 10,
      description: "Seven of Nine is a career engineer: staff-level at Unicorn Startup, founding engineer & CPO at Seed Startup, and systems work on Meta's Bloks infra. Kathryn's earlier roles in product strategy at Unicorn Startup and Google Ops give her technical literacy, but the maximum score is driven by Seven of Nine's deep architecture track record."
    }, {
      subject: 'Vision',
      A: founder1.id === '13' ? 100 : 70,
      B: founder2.id === '13' ? 100 : 70,
      value: 10,
      description: "Kathryn has set product and company direction multiple times: led roadmap for Unicorn Startup Enterprise during 15× customer growth, then became CEO of Breakthrough Startup and shepherded it through an IP sale to Public Company. Seven of Nine contributes founder vision from Seed Startup but this axis is driven mainly by Kathryn's repeat 'big-picture' roles."
    }, {
      subject: 'Execution',
      A: founder1.id === '13' ? 80 : 90,
      B: founder2.id === '13' ? 80 : 90,
      value: 7,
      description: 'Seven of Nine has shipped production systems end-to-end—Seed Startup (0→1 AI procurement), Unicorn Startup procurement infra, and Meta Bloks platform. Kathryn executed operationally at Unicorn Startup and as Breakthrough Startup CEO, but her recent focus has been GTM and board-level strategy, so hands-on build bandwidth lives mostly with Seven of Nine.'
    }, {
      subject: 'Sales',
      A: founder1.id === '13' ? 95 : 65,
      B: founder2.id === '13' ? 95 : 65,
      value: 9,
      description: "Kathryn ran Customer Success at Unicorn Startup and, as Breakthrough Startup CEO, closed the company's early customer base and strategic IP exit. Her VP Products + Marketing stint at Versive adds further GTM depth. Seven of Nine's exposure to direct sales is limited, so the high score comes from Kathryn's commercial leadership."
    }, {
      subject: 'Network',
      A: founder1.previousCompanies.length * 20,
      B: founder2.previousCompanies.length * 20,
      value: Math.round((founder1.previousCompanies.length + founder2.previousCompanies.length) / 2),
      description: 'Combined professional network reach'
    }];
  }
  // Default case for other founder pairs
  return [{
    subject: 'Technical',
    A: getSkillLevel(founder1, 'Technical'),
    B: getSkillLevel(founder2, 'Technical'),
    value: Math.round(Math.max(getSkillLevel(founder1, 'Technical'), getSkillLevel(founder2, 'Technical')) / 10),
    description: `${founder1.archetype === 'CTO' ? founder1.name : founder2.name} brings technical expertise`
  }, {
    subject: 'Vision',
    A: getSkillLevel(founder1, 'Visionary'),
    B: getSkillLevel(founder2, 'Visionary'),
    value: Math.round(Math.max(getSkillLevel(founder1, 'Visionary'), getSkillLevel(founder2, 'Visionary')) / 10),
    description: `${founder1.archetype === 'CEO' ? founder1.name : founder2.name} provides strategic vision`
  }, {
    subject: 'Execution',
    A: getSkillLevel(founder1, 'Executor'),
    B: getSkillLevel(founder2, 'Executor'),
    value: Math.round(Math.max(getSkillLevel(founder1, 'Executor'), getSkillLevel(founder2, 'Executor')) / 10),
    description: 'Execution capability based on work history'
  }, {
    subject: 'Sales',
    A: getSkillLevel(founder1, 'Sales'),
    B: getSkillLevel(founder2, 'Sales'),
    value: Math.round(Math.max(getSkillLevel(founder1, 'Sales'), getSkillLevel(founder2, 'Sales')) / 10),
    description: 'Sales and business development skills'
  }, {
    subject: 'Network',
    A: founder1.previousCompanies.length * 20,
    B: founder2.previousCompanies.length * 20,
    value: Math.round((founder1.previousCompanies.length + founder2.previousCompanies.length) / 2),
    description: 'Combined professional network reach'
  }];
};
// Add new helper functions for the trajectory chart
const getTrajectoryData = (founder1: Founder, founder2: Founder) => {
  // Special case for Divanny Lamas and Kevin Chan
  if (founder1.id === '13' && founder2.id === '9' || founder1.id === '9' && founder2.id === '13') {
    return {
      founder1: [{
        year: 2012,
        company: 'Unicorn Startup',
        role: 'Product Manager – Search & Analytics',
        org_size_entry_bucket: 3,
        valuation_bucket: 4,
        src_notes: '463 emp, FY-11 rev ≈ $0.10 B, IPO S-1 ➜ mkt-cap ≈ $2 B'
      }, {
        year: 2018,
        company: 'Unicorn Startup',
        role: 'Head of NPI',
        org_size_entry_bucket: 4,
        valuation_bucket: 5,
        src_notes: '4 400 emp FY-19, rev ≈ $2.18 B'
      }, {
        year: 2020,
        company: 'Breakthrough Startup',
        role: 'Interim → CEO',
        org_size_entry_bucket: 1,
        valuation_bucket: 2,
        src_notes: "~24 emp ('22 data, earliest public) | $50 m total funding"
      }, {
        year: 2024,
        company: 'Breakthrough Startup – IP sale to Public Company',
        role: 'CEO @ exit',
        org_size_entry_bucket: 2,
        valuation_bucket: 3,
        src_notes: "50-200 team; price undisclosed - industry est 'mid-double-digit $m' (no public filing)"
      }],
      founder2: [{
        year: 2020,
        company: 'Meta',
        role: 'Software Engineer',
        org_size_entry_bucket: 5,
        valuation_bucket: 5,
        src_notes: '58 604 emp 2020 (Macrotrends)'
      }, {
        year: 2022,
        company: 'Seed Startup (co-founder / CPO)',
        role: 'Cofounder',
        org_size_entry_bucket: 1,
        valuation_bucket: 2,
        src_notes: 'Seed-stage, ~7 emp, $6.5 m raise led by Sequoia (pre-money ~ $15-20 m, typical seed)'
      }, {
        year: 2022,
        company: 'Unicorn Startup (fintech)',
        role: 'Staff SE',
        org_size_entry_bucket: 3,
        valuation_bucket: 4,
        src_notes: '≈ 495 emp (Contrary Research) ; Apr-24 valuation $7.65 B (Reuters)'
      }, {
        year: 2024,
        company: 'Unicorn Startup – post Series E',
        role: 'Staff SE / Tech Lead',
        org_size_entry_bucket: 4,
        valuation_bucket: 5,
        src_notes: '~1 100 emp (Wiki) ; Jun-25 valuation $16 B Series E'
      }]
    };
  }
  // Generic data for other founder pairs
  return {
    founder1: generateGenericTrajectoryData(founder1),
    founder2: generateGenericTrajectoryData(founder2)
  };
};
const generateGenericTrajectoryData = (founder: Founder) => {
  // Generate some reasonable trajectory data based on the founder's previous companies
  return founder.previousCompanies.map((company, index) => {
    // Use the start year of each company
    const year = company.years[0];
    // Estimate org size bucket based on company name recognition and role
    let orgSizeBucket = 1;
    if (company.name.includes('Google') || company.name.includes('Meta') || company.name.includes('Facebook') || company.name.includes('Apple')) {
      orgSizeBucket = 5;
    } else if (company.role.includes('Director') || company.role.includes('VP') || company.role.includes('Head')) {
      orgSizeBucket = 3;
    } else if (company.role.includes('Founder')) {
      orgSizeBucket = 1;
    } else {
      orgSizeBucket = 2;
    }
    // Estimate valuation bucket
    let valuationBucket = 1;
    if (company.name.includes('Google') || company.name.includes('Meta') || company.name.includes('Facebook') || company.name.includes('Apple')) {
      valuationBucket = 5;
    } else if (company.name.includes('Scale') || company.name.includes('Databricks') || company.name.includes('Ramp')) {
      valuationBucket = 4;
    } else if (company.role.includes('Founder')) {
      valuationBucket = 1 + index % 3; // Startups grow over time
    } else {
      valuationBucket = 2 + index % 3;
    }
    return {
      year,
      company: company.name,
      role: company.role,
      org_size_entry_bucket: orgSizeBucket,
      valuation_bucket: valuationBucket,
      src_notes: 'Estimated data'
    };
  });
};
const getOrgSizeDescription = (bucket: number): string => {
  switch (bucket) {
    case 1:
      return '< 50 employees';
    case 2:
      return '50-200 employees';
    case 3:
      return '200-1K employees';
    case 4:
      return '1K-5K employees';
    case 5:
      return '> 5K employees';
    default:
      return 'Unknown size';
  }
};
const getValuationDescription = (bucket: number): string => {
  switch (bucket) {
    case 1:
      return '< $10M valuation';
    case 2:
      return '$10M-100M valuation';
    case 3:
      return '$100M-1B valuation';
    case 4:
      return '$1B-10B valuation';
    case 5:
      return '> $10B valuation';
    default:
      return 'Unknown valuation';
  }
};
// Add this function to calculate velocity slope
const calculateVelocitySlope = (data: any[]): number => {
  if (!data || data.length < 2) return 0;
  // Sort data by year
  const sortedData = [...data].sort((a, b) => a.year - b.year);
  // Filter out any entries with missing valuation data
  const validData = sortedData.filter(item => item.valuation_bucket && item.valuation_bucket > 0);
  if (validData.length < 2) return 0;
  const first = validData[0];
  const last = validData[validData.length - 1];
  // Calculate slope as change in valuation / change in years
  const yearDiff = last.year - first.year;
  if (yearDiff === 0) return 0;
  const valueDiff = last.valuation_bucket - first.valuation_bucket;
  return Number((valueDiff / yearDiff).toFixed(1)); // Round to 1 decimal place
};
// Add this function to calculate organization size growth slope
const calculateOrgSizeSlope = (data: any[]): number => {
  if (!data || data.length < 2) return 0;
  // Sort data by year
  const sortedData = [...data].sort((a, b) => a.year - b.year);
  // Filter out any entries with missing organization size data
  const validData = sortedData.filter(item => item.org_size_entry_bucket && item.org_size_entry_bucket > 0);
  if (validData.length < 2) return 0;
  const first = validData[0];
  const last = validData[validData.length - 1];
  // Calculate slope as change in org size / change in years
  const yearDiff = last.year - first.year;
  if (yearDiff === 0) return 0;
  const orgSizeDiff = last.org_size_entry_bucket - first.org_size_entry_bucket;
  return Number((orgSizeDiff / yearDiff).toFixed(1)); // Round to 1 decimal place
};
const isDivannyKevinPair = (founder1: Founder, founder2: Founder): boolean => {
  return founder1.id === '13' && founder2.id === '9' || founder1.id === '9' && founder2.id === '13';
};
// Add this function to calculate company index
const getCompanyIndex = (companyName: string): number => {
  // Create a stable hash of the company name to generate a consistent index
  let hash = 0;
  for (let i = 0; i < companyName.length; i++) {
    const char = companyName.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  // Map the hash to a reasonable range (1-10) for the chart
  const normalizedValue = Math.abs(hash % 10) + 1;
  return normalizedValue;
};
// Add the generateTimelineData function exactly as specified
const generateTimelineData = (founder1: Founder, founder2: Founder) => {
  const years: number[] = [];
  // Collect all years from both founders
  founder1.previousCompanies.forEach(company => {
    for (let year = company.years[0]; year <= company.years[1]; year++) {
      if (!years.includes(year)) years.push(year);
    }
  });
  founder2.previousCompanies.forEach(company => {
    for (let year = company.years[0]; year <= company.years[1]; year++) {
      if (!years.includes(year)) years.push(year);
    }
  });
  years.sort((a, b) => a - b);
  return years.map(year => {
    const founder1Company = founder1.previousCompanies.find(c => year >= c.years[0] && year <= c.years[1]);
    const founder2Company = founder2.previousCompanies.find(c => year >= c.years[0] && year <= c.years[1]);
    return {
      year,
      founder1: founder1Company ? founder1Company.name : null,
      founder2: founder2Company ? founder2Company.name : null
    };
  });
};
// Add the getTimelineDataForChart function
const getTimelineDataForChart = (founder: Founder) => {
  // Sort companies by most recent (end year) first
  const recentCompanies = [...founder.previousCompanies].sort((a, b) => b.years[1] - a.years[1]).slice(0, 6); // Take only the 6 most recent
  // Create a flat array of all years within the date range
  const years: number[] = [];
  const minYear = Math.min(...recentCompanies.map(c => c.years[0]));
  const maxYear = Math.max(...recentCompanies.map(c => c.years[1]));
  for (let year = minYear; year <= maxYear; year++) {
    years.push(year);
  }
  // For each year, create a data point with the company info
  return years.map(year => {
    const company = recentCompanies.find(c => year >= c.years[0] && year <= c.years[1]);
    if (company) {
      return {
        year,
        company: company.name,
        role: company.role,
        value: getCompanyIndex(company.name)
      };
    }
    // Return null for years with no company (gaps in employment)
    return null;
  }).filter(Boolean); // Remove null values
};